import { cloneDeep } from 'lodash-es'
import { BlankLayoutPage, BasicLayoutPage } from 'components/Layout'
const pagesComponent = import.meta.glob('../views/**/*.vue');

/**
 * Permission information
 */
export type PermissionInfo = {
  permission: string;
  actions: string[];
};

/**
 * Button information
 */
export type MenuButtonInfo = {
  id: string;
  name: string;
  permissions: PermissionInfo;
  createTime: number;
  describe?: string;
  options: Record<string, any>;
};


export type MenuItem = {
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Code
   */
  code: string;
  /**
   * Application
   */
  application: string;
  /**
   * Description
   */
  describe: string;
  /**
   * url, route
   */
  url: string;
  /**
   * Icon
   */
  icon: string;
  /**
   * Status, 0 for disabled, 1 for enabled
   */
  status: number;
  /**
   * Bound permission information
   */
  permissions: PermissionInfo[];
  /**
   * Button definition information
   */
  buttons: MenuButtonInfo[];
  /**
   * Other configuration information
   */
  options: Record<string, any>;
  /**
   * Parent ID
   */
  parentId: string;
  /**
   * Tree structure path
   */
  path: string;
  /**
   * Sort index
   */
  sortIndex: number;
  /**
   * Tree level
   */
  level: number;
  createTime: number;
  redirect?: string;
  children?: MenuItem[];
  accessSupport?: { text: string; value: string };
  appId?: string; // Application id
  isShow?: boolean;
  meta?: {
    title?: string
    icon?: string
    [key: string]: any
  },
  component?: any,
  props?: boolean
};

// Extra child routes
const extraRouteObj = {
  'media/Cascade': {
    children: [
      { code: 'Save', name: 'Add' },
      { code: 'Channel', name: 'Select channel' },
    ],
  },
  'media/Device': {
    children: [
      { code: 'Save', name: 'Details' },
      { code: 'Channel', name: 'Channel list' },
      { code: 'Playback', name: 'Playback' },
    ],
  },
  'rule-engine/Scene': {
    children: [
      { code: 'Save', name: 'Details' },
      { code: 'Save2', name: 'Test details' },
    ],
  },
  'rule-engine/Alarm/Configuration': {
    children: [{ code: 'Save', name: 'Details' }],
  },
  'device/Firmware': {
    children: [{ code: 'Task', name: 'Upgrade task' }],
  },
  demo: {
    children: [{ code: 'AMap', name: 'Map' }],
  },
  'system/Platforms': {
    children: [
      { code: 'Api', name: 'Authorization' },
      { code: 'View', name: 'API details' },
    ],
  },
  'system/DataSource': {
    children: [{ code: 'Management', name: 'Management' }],
  },
  'system/Menu': {
    children: [{ code: 'Setting', name: 'Menu configuration' }],
  },
  'system/Apply': {
    children: [
      { code: 'Api', name: 'Authorization' },
      { code: 'View', name: 'API details' },
      { code: 'Save', name: 'Details' },
    ],
  },
  'edge/Device': {
    children: [{ code: 'Remote', name: 'Remote control' }],
  },
  'rule-engine/Alarm/Log': {
    children: [{ code: 'Record', name: 'Processing records' }]
  }
};


const resolveComponent = (name: any) => {
  const importPage = pagesComponent[`../views/${name}/index.vue`];
  if (!importPage) {
    return undefined
  } else {
    const res = () => importPage()
    return res
  }
  //@ts-ignore
}

const findChildrenRoute = (code: string, url: string, routes: any[] = []): MenuItem[] => {
  if (extraRouteObj[code]) {
    const extraRoutes = extraRouteObj[code].children.map((route: MenuItem) => {
      return {
        url: `${url}/${route.code}`,
        code: `${code}/${route.code}`,
        name: route.name,
        isShow: false
      }
    })
    return [...routes, ...extraRoutes]
  }
  return routes
}

const findDetailRouteItem = (code: string, url: string): Partial<MenuItem> | null => {
  const detailComponent = resolveComponent(`${code}/Detail`)
  if (detailComponent) {
    return {
      url: `${url}/detail/:id`,
      code: `${code}/Detail`,
      component: detailComponent,
      name: 'Detail information',
      isShow: false
    }
  }
  return null
}

const findDetailRoutes = (routes: any[]): any[] => {
  const newRoutes: any[] = []
  routes.forEach((route: any) => {
    newRoutes.push(route)
    const detail = findDetailRouteItem(route.code, route.url)
    if (detail) {
      newRoutes.push(detail)
    }
  })
  return newRoutes
}

export const findCodeRoute = (asyncRouterMap: any[]) => {
  const routeMeta = {}

  function getDetail(code: string, url: string) {
    const detail = findDetailRouteItem(code, url)
    if (!detail) return

    routeMeta[(detail as MenuItem).code] = {
      path: detail.url,
      title: detail.name,
      parentName: code,
      buttons: detail.buttons?.map((b: any) => b.id) || []
    }
  }

  function findChildren(data: any[], code: string = '') {
    data.forEach(route => {
      routeMeta[route.code] = {
        path: route.url || route.path,
        title: route.meta?.title || route.name,
        parentName: code,
        buttons: route.buttons?.map((b: any) => b.id) || []
      }
      const otherRoutes = extraRouteObj[route.code]

      if (otherRoutes) {
        otherRoutes.children.map((item: any) => {
          const _code = `${route.code}/${item.code}`
          const url = `${route.url}/${item.code}`
          routeMeta[_code] = {
            path: `${route.url}/${item.code}`,
            title: item.name,
            parentName: route.code,
            buttons: item.buttons?.map((b: any) => b.id) || []
          }
          getDetail(_code, url)
        })
      }
      const _code = route.appId ? `/${route.appId}${route.url}` : route.code
      if (!route.appId) {
        getDetail(_code, route.url)
      } else {
        routeMeta[_code] = {
          path: `/${route.appId}${route.url}`,
          title: route.name,
          parentName: code,
          buttons: []
        }
      }
      if (route.children) {
        findChildren(route.children, _code)
      }
    })
  }

  findChildren(asyncRouterMap)

  return routeMeta
}

export function filterAsyncRouter(asyncRouterMap: any, parentCode = '', level = 1): { menusData: any, silderMenus: any } {
  const _asyncRouterMap = cloneDeep(asyncRouterMap)
  const menusData: any[] = []
  const silderMenus: any[] = []
  _asyncRouterMap.forEach((route: any) => {
    const hasAppId = route.appId
    const _route: any = {
      path:  hasAppId ? `/${route.appId}${route.url}` : `${route.url}`,
      name: hasAppId ? `/${route.appId}${route.url}` : route.code,
      url: hasAppId ? `/${route.appId}${route.url}` : route.url,
      meta: {
        icon: route.icon,
        title: route.name,
        hideInMenu: route.isShow === false,
        buttons: route.buttons?.map((b: any) => b.id) || [],
        isApp: hasAppId,
      },
    }

    const silder = { ..._route }
    // Check for hidden child routes
    route.children = findChildrenRoute(route.code, route.url, route.children)
    route.children = findDetailRoutes(route.children)
    if (route.children && route.children.length) {
      // TODO Check if there is a detail page
      const { menusData: _menusData, silderMenus: _silderMenus } = filterAsyncRouter(route.children, `${parentCode}/${route.code}`, level + 1)
      _route.children = _menusData
      silder.children = _silderMenus
      const showChildren = _route.children.some((r: any) => !r.meta.hideInMenu)

      if (showChildren && _route.children.length) {
        _route.component = level === 1 ? BasicLayoutPage : BlankLayoutPage
        _route.redirect = _route.children[0].url
      } else {
        const myComponent = resolveComponent(route.code)
        // _route.component = myComponent ? myComponent : BlankLayoutPage;
        if (!!myComponent) {
          _route.component = myComponent;
          _route.children.map((r: any) => menusData.push(r))
          delete _route.children
        } else {
          _route.component = BlankLayoutPage
        }
      }
    } else {
      if (hasAppId) {
        _route.component = route.component || resolveComponent('iframe')
      } else {
        _route.component = route.component || resolveComponent(route.code) || BlankLayoutPage
      }
    }
    menusData.push(_route)
    silderMenus.push(silder)
  })
  return {
    menusData,
    silderMenus,
  }
}
